import api from '@/lib/axios';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import type { Booking, BookingFilters, BookingTracking, PaginatedResponse, BookingsListResponse, BookingAPIResponse } from '@/types';

export interface BookingsParams extends BookingFilters {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Helper function to map API response to frontend format
function mapBookingFromAPI(apiBooking: BookingAPIResponse): Booking {
  return {
    id: apiBooking.id,
    bookingNumber: `BK-${apiBooking.id.slice(0, 8)}`,
    customer: {
      id: apiBooking.customer.id,
      name: apiBooking.customer.name,
      phone: apiBooking.customer.phone,
    },
    driver: apiBooking.driver ? {
      id: apiBooking.driver.id,
      profileId: (apiBooking as any).driver_profile_id ?? undefined,
      name: apiBooking.driver.name,
      phone: apiBooking.driver.phone,
      vehicleNumber: apiBooking.driver.vehicle_number || 'N/A',
    } : undefined,
    pickup: {
      address: apiBooking.pickup_address,
      city: apiBooking.city,
      state: 'Odisha',
      pincode: '751001',
      lat: apiBooking.pickup_location.coordinates[1],
      lng: apiBooking.pickup_location.coordinates[0],
    },
    dropoff: {
      address: apiBooking.drop_address,
      city: apiBooking.city,
      state: 'Odisha',
      pincode: '751001',
      lat: apiBooking.drop_location.coordinates[1],
      lng: apiBooking.drop_location.coordinates[0],
    },
    vehicleType: apiBooking.vehicle_type as any,
    status: apiBooking.status as any,
    distance: parseFloat(apiBooking.distance),
    duration: apiBooking.duration,
    fare: {
      baseFare: 0,
      distanceCharge: 0,
      timeCharge: 0,
      total: parseFloat(
        (apiBooking.final_fare && apiBooking.final_fare !== '0'
          ? apiBooking.final_fare
          : apiBooking.estimated_fare) || '0'
      ),
    },
    paidBy: apiBooking.paid_by || 'sender',
    payment: {
      method: (apiBooking.payments?.[0]?.payment_method ?? 'cash') as any,
      status: (
        apiBooking.payments?.[0]?.payment_status === 'completed'
          ? 'paid'
          : apiBooking.status === 'completed' && !apiBooking.payments?.[0]
            ? 'paid'
            : apiBooking.payments?.[0]?.payment_status ?? 'pending'
      ) as any,
      transactionId: apiBooking.payments?.[0]?.transaction_id ?? undefined,
    },
    createdAt: apiBooking.created_at,
    confirmedAt: apiBooking.acceptance_time || undefined,
    pickedUpAt: apiBooking.start_time || undefined,
    deliveredAt: apiBooking.completion_time || undefined,
    cancelledAt: apiBooking.status === 'cancelled' ? apiBooking.updated_at : undefined,
    cancellationReason: apiBooking.cancellation_reason || undefined,
  };
}

export const bookingsApi = {
  getAll: async (params: BookingsParams): Promise<PaginatedResponse<Booking>> => {
    const response = await api.get<BookingsListResponse>(API_ENDPOINTS.BOOKINGS.LIST, {
      params: {
        page: params.page || 1,
        limit: params.limit || 20,
        search: params.search || undefined,
        status: params.status && params.status !== 'all' ? params.status : undefined,
        vehicleType: params.vehicleType && params.vehicleType !== 'all' ? params.vehicleType : undefined,
        paymentStatus: params.paymentStatus && params.paymentStatus !== 'all' ? params.paymentStatus : undefined,
      }
    });
    
    return {
      data: response.data.data.bookings.map(mapBookingFromAPI),
      pagination: {
        page: response.data.data.page,
        limit: params.limit || 20,
        total: response.data.data.total,
        totalPages: response.data.data.pages,
      },
    };
  },

  getById: async (id: string): Promise<Booking> => {
    const response = await api.get(API_ENDPOINTS.BOOKINGS.DETAIL(id));
    const raw: BookingAPIResponse = response.data?.data ?? response.data;
    return mapBookingFromAPI(raw);
  },

  cancel: async (id: string, reason: string): Promise<void> => {
    await api.post(API_ENDPOINTS.BOOKINGS.CANCEL(id), { reason });
  },

  reassign: async (id: string, driverId: string): Promise<void> => {
    await api.post(API_ENDPOINTS.BOOKINGS.REASSIGN(id), { driverId });
  },

  getTracking: async (id: string): Promise<BookingTracking> => {
    const response = await api.get(API_ENDPOINTS.BOOKINGS.TRACKING(id));
    return response.data;
  },
};

// Mock data
export const mockBookings: Booking[] = Array.from({ length: 100 }, (_, i) => {
  const statuses: Booking['status'][] = [
    'pending', 'accepted', 'driver_assigned', 'ongoing', 'completed', 'cancelled'
  ];
  const status = statuses[i % statuses.length];

  return {
    id: String(i + 1),
    bookingNumber: `SKD-2024-${String(i + 1).padStart(4, '0')}`,
    customer: {
      id: String((i % 20) + 1),
      name: ['Rajesh Kumar', 'Priya Sharma', 'Amit Patra', 'Sunita Devi', 'Rahul Mishra'][i % 5],
      phone: `98765${String(i % 1000).padStart(5, '0')}`,
      avatar: i % 3 === 0 ? `https://i.pravatar.cc/150?img=${i + 1}` : undefined,
    },
    driver: status !== 'pending' && status !== 'cancelled' ? {
      id: String((i % 10) + 1),
      name: ['Suresh Patel', 'Ramesh Kumar', 'Bikash Das', 'Manoj Behera', 'Sanjay Singh'][i % 5],
      phone: `98761${String(i % 1000).padStart(5, '0')}`,
      avatar: i % 4 === 0 ? `https://i.pravatar.cc/150?img=${i + 20}` : undefined,
      vehicleNumber: `OD-02-${String.fromCharCode(65 + (i % 26))}${String.fromCharCode(65 + ((i + 1) % 26))}-${1000 + i}`,
    } : undefined,
    pickup: {
      address: ['Sahid Nagar', 'Patia', 'Khandagiri', 'Nayapalli', 'Chandrasekharpur'][i % 5] + ', Bhubaneswar',
      city: 'Bhubaneswar',
      state: 'Odisha',
      pincode: '751' + String(i % 100).padStart(3, '0'),
      lat: 20.296 + (Math.random() * 0.1 - 0.05),
      lng: 85.824 + (Math.random() * 0.1 - 0.05),
    },
    dropoff: {
      address: ['Master Canteen', 'Jaydev Vihar', 'Nandankanan', 'Baramunda', 'Rasulgarh'][i % 5] + ', Bhubaneswar',
      city: 'Bhubaneswar',
      state: 'Odisha',
      pincode: '751' + String((i + 50) % 100).padStart(3, '0'),
      lat: 20.296 + (Math.random() * 0.1 - 0.05),
      lng: 85.824 + (Math.random() * 0.1 - 0.05),
    },
    vehicleType: ['bike', 'auto', 'mini_truck', 'truck'][i % 4] as Booking['vehicleType'],
    status,
    distance: Math.round((5 + Math.random() * 20) * 10) / 10,
    duration: Math.floor(15 + Math.random() * 45),
    fare: {
      baseFare: [30, 50, 100, 200][i % 4],
      distanceCharge: Math.floor(50 + Math.random() * 200),
      timeCharge: Math.floor(20 + Math.random() * 50),
      surge: i % 5 === 0 ? Math.floor(20 + Math.random() * 30) : undefined,
      discount: i % 7 === 0 ? Math.floor(10 + Math.random() * 20) : undefined,
      total: Math.floor(100 + Math.random() * 500),
    },
    payment: {
      method: ['cash', 'upi', 'card', 'wallet'][i % 4] as Booking['payment']['method'],
      status: status === 'completed' ? 'paid' : status === 'cancelled' ? 'refunded' : 'pending',
      transactionId: status === 'completed' ? `TXN${Date.now()}${i}` : undefined,
    },
    createdAt: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
    confirmedAt: status !== 'pending' ? new Date(Date.now() - Math.random() * 29 * 24 * 60 * 60 * 1000).toISOString() : undefined,
    pickedUpAt: ['ongoing', 'completed'].includes(status)
      ? new Date(Date.now() - Math.random() * 28 * 24 * 60 * 60 * 1000).toISOString()
      : undefined,
    deliveredAt: status === 'completed'
      ? new Date(Date.now() - Math.random() * 27 * 24 * 60 * 60 * 1000).toISOString()
      : undefined,
    cancelledAt: status === 'cancelled'
      ? new Date(Date.now() - Math.random() * 27 * 24 * 60 * 60 * 1000).toISOString()
      : undefined,
    cancellationReason: status === 'cancelled' ? 'Customer cancelled' : undefined,
    rating: status === 'completed' && i % 3 !== 0 ? 3 + Math.floor(Math.random() * 3) : undefined,
    feedback: status === 'completed' && i % 4 === 0 ? 'Good service!' : undefined,
  };
});
